import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { FormTextField } from '../../../../design-system/form-text-field/form-text-field';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { CategorySelectField } from './category-select-field';

const MAX_DESCRIPTION_LENGTH = 255;

const formSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  categoryId: z.string({ required_error: 'Category is required' }),
  description: z.string().trim().optional(),
});

type EditVendorFormValues = z.infer<typeof formSchema>;

type EditVendorFormProps = {
  name: string;
  category?: CategoryView;
  categories: CategoryView[];
  showCategorySelect?: boolean;
  description?: string | null;
  isDuplicateName: boolean;
  isLoading: boolean;
  onSubmit: (values: EditVendorFormValues) => void;
  onCancel: () => void;
};

export const EditVendorForm = ({
  name,
  showCategorySelect = true,
  category,
  categories,
  description,
  isDuplicateName,
  isLoading,
  onSubmit,
  onCancel,
}: EditVendorFormProps) => {
  const form = useForm<EditVendorFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name,
      categoryId: category?.id,
      description: description ?? '',
    },
    mode: 'onBlur',
  });

  return (
    <Form {...form}>
      {/* overflow-hidden is constraint form content to fit in the modal */}
      {/* There is probably a better way to do this but couldn't find one */}
      {/* p-1 allows for the focus ring to be visible */}
      <form onSubmit={form.handleSubmit((data) => onSubmit(data))} className="h-full w-full overflow-hidden p-1">
        <Stack space={4} align="stretch" className="h-full bg-white" justify="between">
          <Stack space={4} align="stretch">
            <FormTextField inputName="name" label="Vendor name" placeholder="Type vendor name" />

            {showCategorySelect && (
              <CategorySelectField inputName="categoryId" vendorCategoryId={category?.id} categories={categories} />
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field, formState }) => {
                return (
                  <Stack space={1} align="stretch">
                    <FormItem
                      className={cn(
                        'rounded-lg border border-border bg-white px-4 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:outline-hidden has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-muted',
                        { 'border-destructive': !!formState.errors.description },
                      )}
                    >
                      <Stack space={1} align="stretch">
                        <FormLabel className="text-xs font-medium text-muted-foreground">
                          Description (Optional)
                        </FormLabel>

                        <FormControl>
                          <textarea
                            name="description"
                            placeholder="Type vendor description"
                            value={field.value}
                            onChange={field.onChange}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            className={cn(
                              'w-full resize-none bg-white text-base ring-offset-red-500 placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-transparent focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
                            )}
                          />
                        </FormControl>
                      </Stack>
                    </FormItem>

                    <FormMessage className="w-full text-right" />
                  </Stack>
                );
              }}
            />
          </Stack>

          <Inline space={2} className="w-full">
            <Button
              size="lg"
              type="submit"
              className="flex-1"
              disabled={!form.formState.isValid}
              isLoading={form.formState.isLoading || isLoading}
            >
              {isDuplicateName ? 'Merge transactions' : 'Save'}
            </Button>

            <Button size="lg" variant="secondary" onClick={onCancel} className="flex-1" disabled={isLoading}>
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Form>
  );
};
